import { FolderOpen, GitBranch } from 'lucide-react';
import { useCallback } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useGitFolderStatus } from '@src/hooks/use-git-folder-status';
import { useDependencyForRel } from '@src/hooks/use-dependency-for-rel';
import { DockPointer } from '@src/navigation/DockPointer';
import { useDockNavigation } from '@src/navigation/useDockNavigation';
import { useExplorerComputeNode } from '@src/components/explorer-view/useExplorerComputeNode';
import { normalizeRel } from '@src/components/browseable-tree/adapters/fsFolderRoot';
import { SimpleFileManager } from '@src/components/simple-file-manager';
import { VFSPath } from '@sdk';

interface ContextFolderBrowserProps {
  /** Canonical resource identity from the `fs/vfs/` pointer. */
  vfsPath: VFSPath;
  /** Host navigation (AssetsPage's `navigateAsset`) — re-stamps the active
   *  scope so folder navigation stays in the same assets/project tab. */
  onNavigate: (p: DockPointer) => void;
  /** Scoped project whose dependencies this path belongs to — resolves the
   *  containing git-backed dependency folder for status/push decoration. */
  projectId?: string | null;
  /** Entry of the folder to show selected (the `select` URL option). */
  selectName?: string;
}

/**
 * ContextFolderBrowser — the Assets body for an `fs/<relPath>` pointer: a real
 * file explorer (the Explorer's `SimpleFileManager`) anchored at a project
 * dependency folder. URL-first: navigating into a subfolder rewrites the pointer;
 * double-clicking a file dispatches through `navigation.openFile`.
 *
 * When the browsed path lies inside a GIT-backed dependency folder, the browser
 * decorates: files the remote doesn't have yet render in amber, and a slim
 * header strip shows the branch + an unpushed badge. The git ACTIONS (changes
 * diff + push/notify) live on the folder's tree row — see
 * {@link ContextFolderGitBadge} — not in this pane.
 */
export function ContextFolderBrowser({ vfsPath, onNavigate, projectId, selectName }: ContextFolderBrowserProps) {
  const { t } = useLingui();
  const { navigation } = useDockNavigation();
  // The VFS the file manager browses. `useDependencyForRel` resolves the same
  // compute node for its git-ops workdir, but the manager needs the TypeId itself.
  const { typeId } = useExplorerComputeNode();

  const rel = normalizeRel(vfsPath.entitySubPath);
  const initialPath = rel ? `/${rel}` : '/';

  // The dependency folder containing the browsed path; this pane only decorates the
  // GIT-backed ones, so a local folder reads as "no workdir" exactly as before.
  const folder = useDependencyForRel(projectId, rel);
  const gitWorkdir = folder?.originKind === 'git' ? folder.workdir : null;

  const { status, hasUnpushed, isPathUnpushed, refresh } = useGitFolderStatus(
    gitWorkdir,
    folder?.computeNodeId ?? '@local',
  );

  const handlePathChange = useCallback(
    (vfs: string) => {
      const locatorTypeId = vfsPath.typeId;
      if (!locatorTypeId) return;
      // SimpleFileManager reports a full VFS path on the LIVE compute node;
      // re-stamp it onto the root's durable locator typeId.
      onNavigate(DockPointer.forAssetFs(VFSPath.fromTypeId(locatorTypeId, VFSPath.parse(vfs).entitySubPath)));
    },
    [onNavigate, vfsPath],
  );

  const handleFileSelect = useCallback(
    (path: string) => {
      // Extension dispatch (md → assets document viewer, else code editor)
      // lives in openFile — mirror the Explorer body.
      navigation.openFile(path);
    },
    [navigation],
  );

  if (!typeId) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <FolderOpen className="h-12 w-12 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">
          <Trans>No compute node available</Trans>
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {gitWorkdir && (
        <div
          className="flex items-center justify-between gap-2 border-b border-border/50 px-3 py-1.5"
          data-testid="context-folder-git-bar"
        >
          <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
            <GitBranch className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">{status?.branch ?? t`git`}</span>
            {hasUnpushed && (
              <span className="flex-shrink-0 rounded-full bg-amber-500/15 px-2 py-px text-[10px] font-medium text-amber-600 dark:text-amber-400">
                <Trans>Unpushed changes</Trans>
              </span>
            )}
          </span>
        </div>
      )}
      <div className="min-h-0 flex-1">
        <SimpleFileManager
          typeId={typeId}
          initialPath={initialPath}
          initialSelectedName={selectName}
          onPathChange={handlePathChange}
          onFileSelect={handleFileSelect}
          onFsMutated={gitWorkdir ? () => void refresh() : undefined}
          isPathHighlighted={gitWorkdir ? isPathUnpushed : undefined}
          className="h-full"
        />
      </div>
    </div>
  );
}
